import {
	IS_MOBILE_VERIFIED_KEY,
	IS_PIN_ENABLED_KEY,
	PIN_SETUP_LOGIN_SESSION_KEY,
	PIN_UNLOCK_LOGIN_SESSION_KEY,
} from '../assets/constants'

/** In-memory only: refresh drops grant so set-PIN requires a new OTP. */
let pinSetupRequestIdMemory: string | null = null
let pinSetupOtpMemory: string | null = null

function setBoth(key: string, value: string) {
	if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(key, value)
	if (typeof localStorage !== 'undefined') localStorage.setItem(key, value)
}

function removeBoth(key: string) {
	if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(key)
	if (typeof localStorage !== 'undefined') localStorage.removeItem(key)
}

function getEither(key: string): string | null {
	if (typeof sessionStorage !== 'undefined') {
		const s = sessionStorage.getItem(key)
		if (s !== null) return s
	}
	if (typeof localStorage !== 'undefined') {
		return localStorage.getItem(key)
	}
	return null
}

export function isPinEnabledFlag(): boolean {
	return getEither(IS_PIN_ENABLED_KEY) === '1'
}

export function setPinEnabledFlag(enabled: boolean) {
	setBoth(IS_PIN_ENABLED_KEY, enabled ? '1' : '0')
}

export function isMobileVerifiedFlag(): boolean {
	return getEither(IS_MOBILE_VERIFIED_KEY) === '1'
}

export function setMobileVerifiedFlag(verified: boolean) {
	setBoth(IS_MOBILE_VERIFIED_KEY, verified ? '1' : '0')
}

/**
 * Matches server PinSessionService: unlock gate only after mobile verified + PIN set.
 * Otherwise JWT is onboarding and /auth/verify-pin is blocked.
 */
export function isPinSessionUnlockEligible(): boolean {
	return isPinEnabledFlag() && isMobileVerifiedFlag()
}

export function readPinGateFromUser(user: unknown): {
	pinEnabled: boolean
	mobileVerified: boolean
	unlockEligible: boolean
} {
	if (!user || typeof user !== 'object') {
		return { pinEnabled: false, mobileVerified: false, unlockEligible: false }
	}
	const o = user as Record<string, unknown>
	const pinEnabled = Boolean(o.isPinEnabled ?? o.is_pin_enabled)
	const mobileVerified = Boolean(o.mobileVerified ?? o.mobile_verified)
	return {
		pinEnabled,
		mobileVerified,
		unlockEligible: pinEnabled && mobileVerified,
	}
}

export function applyPinEnabledFromUserProfile(user: unknown) {
	if (!user || typeof user !== 'object') return
	const o = user as Record<string, unknown>
	const pin = o.isPinEnabled ?? o.is_pin_enabled
	if (typeof pin === 'boolean') {
		setPinEnabledFlag(pin)
	}
	const mobile = o.mobileVerified ?? o.mobile_verified
	if (typeof mobile === 'boolean') {
		setMobileVerifiedFlag(mobile)
	}
	const { unlockEligible } = readPinGateFromUser(user)
	if (!unlockEligible) {
		clearPinUnlockPending()
	}
}

export function isPinUnlockPending(): boolean {
	return getEither(PIN_UNLOCK_LOGIN_SESSION_KEY) === '1'
}

export function setPinUnlockPending() {
	setBoth(PIN_UNLOCK_LOGIN_SESSION_KEY, '1')
}

export function clearPinUnlockPending() {
	removeBoth(PIN_UNLOCK_LOGIN_SESSION_KEY)
}

export function isPinSetupPending(): boolean {
	return getEither(PIN_SETUP_LOGIN_SESSION_KEY) === '1'
}

/** @deprecated Setup is in-app; kept for clearing legacy flags. */
export function setPinSetupPending() {
	setBoth(PIN_SETUP_LOGIN_SESSION_KEY, '1')
}

export function clearPinSetupPending() {
	removeBoth(PIN_SETUP_LOGIN_SESSION_KEY)
}

/** True when login page must stay open for PIN unlock only. */
export function isPinAuthGatePending(): boolean {
	return isPinUnlockPending()
}

export type PinSetupGrant = {
	requestId: string
	otp: string
}

/** Store requestId_2 + the SMS OTP the user just typed (not persisted). */
export function setPinSetupGrant(requestId: string, otp: string) {
	pinSetupRequestIdMemory = requestId || null
	pinSetupOtpMemory = otp || null
}

/** @deprecated Use setPinSetupGrant(requestId, otp). */
export function setPinSetupToken(token: string) {
	setPinSetupGrant(token, pinSetupOtpMemory || '')
}

export function getPinSetupGrant(): PinSetupGrant | null {
	if (!pinSetupRequestIdMemory || !pinSetupOtpMemory) return null
	return { requestId: pinSetupRequestIdMemory, otp: pinSetupOtpMemory }
}

export function getPinSetupToken(): string | null {
	return getPinSetupGrant()?.requestId ?? null
}

export function clearPinSetupToken() {
	pinSetupRequestIdMemory = null
	pinSetupOtpMemory = null
}

export function clearPinBrowserFlags() {
	removeBoth(IS_PIN_ENABLED_KEY)
	removeBoth(IS_MOBILE_VERIFIED_KEY)
	removeBoth(PIN_UNLOCK_LOGIN_SESSION_KEY)
	removeBoth(PIN_SETUP_LOGIN_SESSION_KEY)
	clearPinSetupToken()
}
